import type {
  LibraryArticleRecord,
  SourcePermissionBadge,
  SourcePermissionState,
  SavedFromSource,
} from '../types/libraryProvenance';

export function normalizeSavedFrom(raw: unknown, source?: string): SavedFromSource {
  const savedFromRaw = String(raw ?? '').toLowerCase();
  const sourceRaw = String(source ?? '').toLowerCase();

  if (
    savedFromRaw.includes('litportal') ||
    sourceRaw.includes('litportal') ||
    savedFromRaw === 'litportal-library' ||
    sourceRaw === 'litportal-library'
  ) {
    return 'litportal-library';
  }

  const value = savedFromRaw || sourceRaw;
  if (value.includes('search')) return 'search';
  if (value.includes('digest') || value.includes('screen')) return value.includes('screen') ? 'screen' : 'digest';
  if (value.includes('litscholar') || value.includes('scholar')) return 'litscholar';
  if (value === 'manual') return 'manual';
  return 'unknown';
}

/** Provenance used for LitArchive filters — considers both saved_from and source. */
export function resolveArticleSavedFrom(
  article: Pick<LibraryArticleRecord, 'saved_from' | 'source'>
): SavedFromSource {
  return normalizeSavedFrom(article.saved_from, article.source);
}

export function isLitPortalSource(
  article: Pick<LibraryArticleRecord, 'saved_from' | 'source'>
): boolean {
  const src = (article.source || '').toLowerCase();
  return (
    resolveArticleSavedFrom(article) === 'litportal-library' ||
    src === 'litportal-library' ||
    src.includes('litportal')
  );
}

/**
 * LitArchive Source filter:
 * - `all` — no filter
 * - `litpulse` — everything except LitPortal
 * - `litportal-library` — LitPortal saves only
 */
export function articleMatchesSavedFromFilter(
  article: LibraryArticleRecord,
  filter: string
): boolean {
  if (filter === 'all') return true;
  if (filter === 'litpulse') return !isLitPortalSource(article);
  if (filter === 'litportal-library') return isLitPortalSource(article);
  return resolveArticleSavedFrom(article) === filter;
}

export function savedFromLabel(savedFrom: SavedFromSource | string): string {
  if (savedFrom === 'litpulse') return 'LitCurrent';
  const labels: Record<SavedFromSource, string> = {
    search: 'LitCurrent',
    digest: 'LitCurrent',
    screen: 'LitCurrent',
    litscholar: 'LitCurrent',
    'litportal-library': 'LitPortal',
    manual: 'LitCurrent',
    unknown: 'LitCurrent',
  };
  return labels[savedFrom as SavedFromSource] ?? String(savedFrom);
}

export function mapApiArticleToRecord(a: Record<string, unknown>): LibraryArticleRecord {
  const pubDate = a.pub_date ? String(a.pub_date) : undefined;
  let year: number | undefined;
  if (pubDate) {
    const y = new Date(pubDate).getFullYear();
    if (!Number.isNaN(y)) year = y;
  }
  if (typeof a.year === 'number') year = a.year;

  return {
    pmid: String(a.pmid || ''),
    title: String(a.title || ''),
    abstract: a.abstract ? String(a.abstract) : undefined,
    journal: a.journal ? String(a.journal) : undefined,
    authors: a.authors ? String(a.authors) : undefined,
    pub_date: pubDate,
    ai_summary: a.ai_summary ? String(a.ai_summary) : undefined,
    specialty_name: a.specialty_name ? String(a.specialty_name) : undefined,
    specialty_id: a.specialty_id ? String(a.specialty_id) : undefined,
    subspecialty_id: a.subspecialty_id ? String(a.subspecialty_id) : undefined,
    profile_id: (a.profile_id || a.digest_profile_id) ? String(a.profile_id || a.digest_profile_id) : undefined,
    design_tags: Array.isArray(a.design_tags) ? (a.design_tags as string[]) : undefined,
    saved_at: a.saved_at ? String(a.saved_at) : undefined,
    folder: a.folder ? String(a.folder) : 'Ungrouped',
    doi: a.doi ? String(a.doi) : undefined,
    year,
    saved_from: normalizeSavedFrom(a.saved_from, a.source as string),
    source: a.source ? String(a.source) : undefined,
    clinical_question: a.clinical_question ? String(a.clinical_question) : undefined,
    search_session_id: a.search_session_id ? String(a.search_session_id) : undefined,
    evidence_packet_id: a.evidence_packet_id ? String(a.evidence_packet_id) : undefined,
    ranker_advisory: a.ranker_advisory as LibraryArticleRecord['ranker_advisory'],
    source_permission: a.source_permission as LibraryArticleRecord['source_permission'],
    full_text_status: a.full_text_status as LibraryArticleRecord['full_text_status'],
    citation_verified: a.citation_verified === true,
    eligible_for_summary: a.eligible_for_summary !== false,
  };
}

export function resolveSourcePermissions(article: LibraryArticleRecord): SourcePermissionState {
  const badges: SourcePermissionBadge[] = [];
  const restricted = article.source_permission === 'restricted';
  const metadataOnly = restricted || article.eligible_for_summary === false;
  const linkOutOnly = restricted && article.full_text_status !== 'available' && article.full_text_status !== 'uploaded';

  if (article.citation_verified) badges.push('citation_verified');
  if (article.full_text_status === 'uploaded') badges.push('uploaded_full_text');
  else if (article.full_text_status === 'available') badges.push('full_text_available');

  if (metadataOnly || linkOutOnly) {
    badges.push(linkOutOnly ? 'link_out_only' : 'metadata_only');
  } else if (!restricted && article.eligible_for_summary !== false) {
    badges.push('ai_summary_eligible');
  }

  const canLitScholar = !metadataOnly && !linkOutOnly;
  const canAudio = !metadataOnly;

  return {
    badges,
    canAudio,
    canLitScholar,
    blockedAudioReason: !canAudio ? 'Audio summary requires more than metadata-only access.' : undefined,
    blockedLitScholarReason: !canLitScholar
      ? linkOutOnly
        ? 'LitScholar deep dive unavailable — link-out only for this source.'
        : 'LitScholar unavailable — metadata-only source permissions.'
      : undefined,
  };
}

export function permissionBadgeLabel(badge: SourcePermissionBadge): string {
  const labels: Record<SourcePermissionBadge, string> = {
    ai_summary_eligible: 'AI-summary eligible',
    link_out_only: 'Link-out only',
    metadata_only: 'Metadata only',
    full_text_available: 'Full text available',
    uploaded_full_text: 'Uploaded full text',
    citation_verified: 'Citation verified',
  };
  return labels[badge];
}

export function permissionBadgeClass(badge: SourcePermissionBadge): string {
  switch (badge) {
    case 'ai_summary_eligible':
    case 'citation_verified':
    case 'full_text_available':
    case 'uploaded_full_text':
      return 'bg-emerald-50 text-emerald-800 border-emerald-200';
    case 'link_out_only':
    case 'metadata_only':
      return 'bg-amber-50 text-amber-800 border-amber-200';
    default:
      return 'bg-slate-50 text-slate-600 border-slate-200';
  }
}
